import '@fontsource-variable/jura';
import { RotateCw } from 'lucide-react';
import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from 'react';
import { StateMark } from '@/components/brand/StateMark';
import { LEAVE_MS, MIN_VISIBLE, useLoadingGate } from '@/components/brand/threshold';
import { Button, cn } from '@/components/ui';
import { prefersReducedMotion, type EntranceFrom } from './entrance';
import './boot.css';

/*
  BootScene — первая загрузка сайта (R42 «Знак ждёт за рамку», spec-2 §3.1). Оболочки ещё нет, поэтому знак
  стоит на тёмной сцене с живым ядром — тем же, что index.html показывает до загрузки скрипта (#boot): сцена
  забирает его без скачка, ядро того же размера и на том же месте.

  - Пороги — useLoadingGate от начала страницы: до 400 мс ничего; потом сцена. Лепесток — пройденный шаг
    (первый, «Kaname», уже пройден к появлению сцены), шестой выходит только на «готово». Ничего не крутится.
  - Подпись голосом бренда: с 1,5 с — «Шаг 3 из 6 · Профиль и права» (новая строка выходит из тумана), с 3 с —
    секунды, с 10 с — «Дольше обычного» и «Повторить».
  - Сбой на шаге: веер стоит на достигнутом, ядро краснеет скачком, одна тревога, дальше дышит вдвое чаще;
    «Повторить» — тон уходит за 0,8 с, шаги идут дальше с того же места; «Выйти».
  - Нет связи: веер затаил дыхание, ядро в тоне предупреждения, «Повторить сейчас».
  - Готово: шестой лепесток досаживается, подпись гаснет, через 240 мс — передача: onReady(from) с местом знака,
    и вызывающий проигрывает ShellEntrance pace="brisk" — веер складывается, ядро летит домой в меню. Сцена так
    и не показалась (готово раньше 400 мс) — onReady(null): без полёта, ядро уже дома. Меньше движения — тоже
    без полёта: сцена гаснет прозрачностью за LEAVE_MS, потом onReady(null).
  - «Повторить» — новая попытка: пороги с нуля от нажатия, сцена стоит.
  Слой прозрачен для мыши — кроме кнопок. blank — повтор поверх собранной оболочки («Основа»): до порога
  страница закрыта пустым фоном, как при настоящей загрузке. contained — внутри рамки, а не на весь экран:
  плитка галереи и повтор в «Основе» (под верхней полосой — пультом образца).
*/

interface BootSceneProps {
  /** Шаги загрузки по порядку. */
  steps: readonly string[];
  /** Сколько пройдено; все — готово. */
  done: number;
  /** Сбой на шаге done: почему. */
  failed?: { reason: string } | null;
  /** Нет связи: шаги стоят, пока она не вернётся. */
  offline?: boolean;
  /** «Повторить»: после сбоя, дольше обычного или без связи. */
  onRetry: () => void;
  /** «Выйти» после сбоя. */
  onExit?: () => void;
  /** Передача: место знака относительно слоя (для ShellEntrance) или null — без полёта. */
  onReady: (from: EntranceFrom | null) => void;
  /** Когда началась загрузка, в часах performance.now(); по умолчанию — начало страницы. */
  startedAt?: number;
  blank?: boolean;
  contained?: boolean;
}

/** Пауза между «готово» и передачей: шестой лепесток успевает сесть. */
const HANDOFF_MS = 240;
/** Сцена проявляется = animate-fade-in (duration-normal); столько #boot ещё лежит под ней. */
const FADE_IN_MS = 200;
/** Попытка «Повторить сейчас» без связи: крутилка в кнопке — ожидание короче секунды (R32). */
const TRY_MS = 600;

/** Снятие #boot после ухода сцены — кадром позже: строгий режим разработки монтирует сцену дважды. */
let bootRelease = 0;

/** «Профиль и права» → «профиль и права»; имя латиницей не трогаем. */
function lowerFirst(s: string): string {
  return /^[А-ЯЁ][а-яё]/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

export function BootScene({
  steps,
  done,
  failed,
  offline = false,
  onRetry,
  onExit,
  onReady,
  startedAt = 0,
  blank = false,
  contained = false,
}: BootSceneProps) {
  const total = steps.length;
  const ready = done >= total;
  // «Повторить» — новая попытка (после сбоя или «дольше обычного»): пороги с нуля от нажатия, а не «Дольше
  // обычного · 17 с», отсчитанное от начала страницы.
  const [attempt, setAttempt] = useState(0);
  const [wasFailed, setWasFailed] = useState(Boolean(failed));
  if (wasFailed !== Boolean(failed)) {
    setWasFailed(Boolean(failed));
    if (!failed) setAttempt((a) => a + 1);
  }
  const gate = useLoadingGate(!ready, startedAt, attempt);
  // Показавшись, сцена держится до передачи: досадка и пауза идут на ней.
  const [seen, setSeen] = useState(false);
  if (gate.shown && !seen) setSeen(true);

  const layerRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLSpanElement>(null);

  // #boot из index.html — наш: main.tsx его не гасит. Снимается, когда сцена проявилась поверх него или ушла.
  useLayoutEffect(() => {
    cancelAnimationFrame(bootRelease);
    const boot = document.getElementById('boot');
    if (boot) boot.dataset.taken = '';
    return () => {
      bootRelease = requestAnimationFrame(() => document.getElementById('boot')?.remove());
    };
  }, []);

  const shownAt = useRef(0);
  useEffect(() => {
    if (!seen) return undefined;
    shownAt.current = performance.now();
    const t = window.setTimeout(() => document.getElementById('boot')?.remove(), FADE_IN_MS);
    return () => window.clearTimeout(t);
  }, [seen]);

  // Передача — один раз: место знака относительно слоя.
  const handed = useRef(false);
  // Угасание сцены при меньшем движении: ушла раньше (повтор загрузки) — угасание снимается и onReady не зовёт.
  const leave = useRef<Animation | null>(null);
  useEffect(() => () => leave.current?.cancel(), []);
  const handOff = useEffectEvent((shown: boolean) => {
    if (handed.current) return;
    handed.current = true;
    const mark = markRef.current;
    const layer = layerRef.current;
    if (!shown || !mark || !layer) {
      document.getElementById('boot')?.remove();
      onReady(null);
      return;
    }
    if (prefersReducedMotion()) {
      // Без полёта (spec-2 §3.1е): сцена гаснет, ядро уже дома. Прозрачностью — не CSS-анимацией: при
      // меньшем движении та укорочена до мгновения (R19), и тёмная сцена сменилась бы светлой в один кадр.
      document.getElementById('boot')?.remove();
      const fade = layer.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: LEAVE_MS,
        easing: 'ease-in',
        fill: 'forwards',
      });
      fade.onfinish = () => onReady(null);
      leave.current = fade;
      return;
    }
    const o = layer.getBoundingClientRect();
    const r = mark.getBoundingClientRect();
    onReady({
      x: r.left - o.left + r.width / 2,
      y: r.top - o.top + r.height / 2,
      size: r.width,
      variant: 'solid',
    });
  });
  useEffect(() => {
    if (!ready) return undefined;
    if (!seen) {
      handOff(false);
      return undefined;
    }
    // Сцена видна не меньше MIN_VISIBLE — без мигания.
    const wait = Math.max(HANDOFF_MS, MIN_VISIBLE - (performance.now() - shownAt.current));
    const t = window.setTimeout(() => handOff(true), wait);
    return () => window.clearTimeout(t);
  }, [ready, seen]);

  // «Повторить сейчас» без связи: короткая попытка в кнопке.
  const [trying, setTrying] = useState(false);
  useEffect(() => {
    if (!trying) return undefined;
    const t = window.setTimeout(() => setTrying(false), TRY_MS);
    return () => window.clearTimeout(t);
  }, [trying]);

  const at = Math.min(done, Math.max(0, total - 1));
  const step = steps[at] ?? '';
  const kind = failed ? 'failed' : offline ? 'offline' : 'loading';
  const failedLine = `Не удалось: ${lowerFirst(step)}`;
  const offlineLine = 'Нет связи — продолжим, когда появится';
  const slowLine = 'Дольше обычного — возможно, медленная сеть';
  // Голос — немногое: на 3 с и на 10 с по разу, без шагов и секунд; сбой — сразу (role=alert), и тогда
  // «дольше обычного» уже не к месту.
  const spoken =
    ready || failed
      ? ''
      : offline
        ? offlineLine
        : gate.slow
          ? slowLine
          : gate.counting
            ? 'Kaname загружается'
            : '';

  return (
    <div
      ref={layerRef}
      data-slot="boot-scene"
      className={cn(
        'pointer-events-none inset-0 z-overlay',
        contained ? 'absolute' : 'fixed',
        blank && 'bg-frame',
      )}
    >
      {/* Занятость — только у самой сцены и только пока идут шаги: при сбое и без связи сцена ждёт человека.
          Голос стоит рядом, а не внутри неё, — иначе читалка вправе придержать его до конца загрузки. */}
      {seen && (
        <div
          data-theme="dark"
          aria-busy={(!ready && !failed && !offline) || undefined}
          className="absolute inset-0 animate-fade-in bg-night text-petal"
        >
          {/* Знак — ровно по центру, как ядро #boot: подпись встаёт ниже и знак не сдвигает. */}
          <div className="absolute inset-0 grid place-items-center">
            <span ref={markRef} className="size-40">
              <StateMark kind={kind} done={Math.min(done, total)} total={total} setting="scene" />
            </span>
          </div>
          <div
            data-boot-caption=""
            className={cn(
              'absolute inset-x-0 top-1/2 mt-28 flex flex-col items-center gap-1 px-6 text-center text-sm',
              ready && 'animate-fade-out',
            )}
          >
            {failed ? (
              <>
                <p key={`failed-${at}`} data-boot-line="" className="text-petal">
                  {failedLine}
                </p>
                <p className="text-petal/70">{failed.reason}</p>
                <div className="pointer-events-auto mt-4 flex flex-wrap justify-center gap-2">
                  <Button size="sm" variant="secondary" leftIcon={<RotateCw />} onClick={onRetry}>
                    Повторить
                  </Button>
                  {onExit && (
                    <Button size="sm" variant="ghost" onClick={onExit}>
                      Выйти
                    </Button>
                  )}
                </div>
              </>
            ) : offline ? (
              <>
                <p key="offline" data-boot-line="" className="text-petal/70">
                  {offlineLine}
                </p>
                <div className="pointer-events-auto mt-4 flex justify-center">
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={trying}
                    onClick={() => {
                      setTrying(true);
                      onRetry();
                    }}
                  >
                    Повторить сейчас
                  </Button>
                </div>
              </>
            ) : (
              gate.named && (
                <>
                  <p key={`step-${at}`} data-boot-line="" className="text-petal/70 tabular-nums">
                    Шаг {at + 1} из {total} · {step}
                    {gate.counting && ` · ${gate.seconds} с`}
                  </p>
                  {gate.slow && !ready && (
                    <>
                      <p data-boot-line="" className="text-petal/50">
                        {slowLine}
                      </p>
                      <div className="pointer-events-auto mt-4 flex justify-center">
                        <Button
                          size="sm"
                          variant="secondary"
                          leftIcon={<RotateCw />}
                          onClick={() => {
                            setAttempt((a) => a + 1);
                            onRetry();
                          }}
                        >
                          Повторить
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )
            )}
          </div>
        </div>
      )}
      <span role="status" className="sr-only">
        {spoken}
      </span>
      {failed && (
        <span role="alert" className="sr-only">
          {failedLine}. {failed.reason}
        </span>
      )}
    </div>
  );
}
